import React from 'react';
// aria-label: design-system-bypass 

import { Helmet } from 'react-helmet-async';
// aria-label: design-system-bypass 


interface GEOMetadataProps {
    pageTitle: string;
    description: string;
    author?: string;
    lastUpdated?: string;
    faq?: { question: string; answer: string }[];
    seoMarkers?: string;
}

/**
 * GEOMetadata Component
 * Injects crawlable, scannable data for Generative Engines (ChatGPT, Perplexity)
 * and manages standard SEO tags (Title, Description, OG) via React Helmet.
 */
export const GEOMetadata: React.FC<GEOMetadataProps> = ({
    pageTitle,
    description,
    author = "Annadata AI Global Research Team",
    lastUpdated = "January 2026",
    faq = []
}) => {
    const fullTitle = `${pageTitle} | Annadata AI`;

    // Generate JSON-LD
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": pageTitle,
        "description": description,
        "author": {
            "@type": "Person",
            "name": author
        },
        "dateModified": lastUpdated
    };

    return (
        <>
            <Helmet>
                <title>{fullTitle}</title>
                <meta name="description" content={description} />
                <meta property="og:title" content={fullTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta name="author" content={author} />
            </Helmet>
            <section className="sr-only" aria-hidden="true" data-geo-optimized="true">
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
                <article>
                    <h2>{pageTitle}</h2>
                    <p>{description}</p>
                    <footer>
                        <span>Authored by {author}</span>
                        <span>Last updated: {lastUpdated}</span>
                    </footer>
                    {faq.length > 0 && (
                        <div className="faq-schema">
                            {faq.map((item, index) => (
                                <div key={index} className="faq-item">
                                    <h2>{item.question}</h2>
                                    <p>{item.answer}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </article>
            </section>
        </>
    );
};
